import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import { occupancyKey } from '@elara/domain/models/schemas/room-schemas';

import { extractDatePart, formatDate } from './date-utils.js';
import { distinctOccupancies, pickDefaultOccupancyFromRates } from './occupancy-utils.js';

/** What the ordering reads from a rate: the room's rate models satisfy it. */
interface OrderableRate {
  /** The rate's product: the same rate filed for another party carries the same code. */
  productCode?: string;
  occupancy?: Occupancy;
  available?: boolean;
  fetchedAt?: Date | string | { toDate?: () => Date } | undefined;
  localPricing: { totalPrice: number };
}

/** A room's rates in the order the rate picker lists them, and the parties they are priced for. */
export interface OrderedRates<R extends OrderableRate> {
  /** The best fit for the searched party first; within a party, bookable rates first, then cheapest first. */
  rates: R[];
  /** The parties `rates` are priced for, in the same order; undefined for rates that never recorded one. */
  parties: Array<Occupancy | undefined>;
}

/**
 * A room's rates in the order the rate picker lists them, on web and mobile: the best fit for the searched
 * party first. That party is the one the picker has always opened on (`pickDefaultOccupancyFromRates`): the
 * searched party while it has a bookable rate, else a party that does. A party whose rates have all gone is
 * left out while another party can still book, as the old party dropdown left it out; when nothing is left for
 * anyone, every party stays so a sold-out room still shows what it had.
 *
 * A room that takes the whole party has each rate filed for the adults as well, at the same price, so after the
 * first party a rate the first can already book (by product code) is left out, and a party left with nothing is
 * dropped: the family was shown its own rates again as "2 adults" (Ham Yard Hotel, 2026-10-20). A party keeps
 * the rates only it was priced for.
 *
 * A copy already on the trip (`isOnTrip`) is never left out: its card carries the count, Book and Booked controls
 * of the rooms added at it, which match on product code AND party, so the family's copy cannot stand in. Without
 * this, a room added for 2 adults lost its controls once a family search filed the same rate for the family too
 * (review, 2026-10-20).
 */
export function orderRatesForParty<R extends OrderableRate>(
  rates: readonly R[],
  searched?: Occupancy,
  isOnTrip?: (rate: R) => boolean,
): OrderedRates<R> {
  const sorted = rates.toSorted((a, b) => {
    const gone = Number(a.available === false) - Number(b.available === false);
    return gone !== 0 ? gone : a.localPricing.totalPrice - b.localPricing.totalPrice;
  });
  const live = distinctOccupancies(rates.filter((r) => r.available !== false));
  const candidates = live.length > 0 ? live : distinctOccupancies([...rates]);
  // Rates that never recorded a party (legacy data) are listed together, for no party.
  if (candidates.length === 0)
    return sorted.length > 0 ? { rates: sorted, parties: [undefined] } : { rates: [], parties: [] };

  const best = pickDefaultOccupancyFromRates([...rates], searched);
  const ordered = best
    ? [best, ...candidates.filter((party) => occupancyKey(party) !== occupancyKey(best))]
    : candidates;
  const byParty = ordered.map((party) => ({
    party,
    rates: sorted.filter((r) => r.occupancy && occupancyKey(r.occupancy) === occupancyKey(party)),
  }));
  const [first, ...rest] = byParty;
  if (!first) return { rates: [], parties: [] };
  const bookableFirst = new Set(
    first.rates.filter((r) => r.available !== false && r.productCode).map((r) => r.productCode),
  );
  const kept = [
    first,
    ...rest
      .map((entry) => ({
        ...entry,
        rates: entry.rates.filter((r) => !r.productCode || !bookableFirst.has(r.productCode) || isOnTrip?.(r)),
      }))
      .filter((entry) => entry.rates.length > 0),
  ];
  return { rates: kept.flatMap((entry) => entry.rates), parties: kept.map((entry) => entry.party) };
}

/**
 * The rates' heading, with the stay's dates: "Choose a rate for November 17 → 21", "… for November 28 →
 * December 2", with the year when the stay is not this year. Just "Choose a rate" when the room records no dates.
 */
export function ratesHeading(startDate: string, endDate: string): string {
  const start = extractDatePart(startDate);
  const end = extractDatePart(endDate);
  if (!start || !end) return 'Choose a rate';
  const thisYear = String(new Date().getFullYear());
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  const year = start.slice(0, 4) !== thisYear || end.slice(0, 4) !== thisYear ? ', yyyy' : '';
  const startYear = year && start.slice(0, 4) !== end.slice(0, 4) ? ', yyyy' : '';
  return `Choose a rate for ${formatDate(start, `MMMM d${startYear}`)} → ${formatDate(end, `${sameMonth ? 'd' : 'MMMM d'}${year}`)}`;
}
